import { MutableRefObject, useContext, useEffect, useState } from 'react';
import { findNodeHandle } from 'react-native';

import { useNodeHandle } from './useNodeHandle';

import { AppContext } from '../AppProvider';
import { PageContext } from '../store/PageContextProvider';

/**
 * Returns context reference from AppContext. Should be used for pages and main menu.
 * @param {*} id The testID
 * @returns Context reference if found for that id, otherwise null
 */
export const useAppContextReference = id => {
  // Takes in testID and returns a global reference
  const [appContext] = useContext(AppContext) || [];

  return (appContext?.globalReferences && appContext.globalReferences[id]) || null;
};

/**
 * Returns all context references from AppContext. Should be used for pages and main menu.
 * @returns All context references from the AppContext, in dictionary form. The keys are
 * each id, and the values are the refs.
 */
export const useAllAppContextReferences = () => {
  const [context] = useContext(AppContext) || [];

  return context?.globalReferences || [];
};

/**
 * Returns node ID/handle from AppContext. Should be used for pages and main menu.
 * @param {*} id The testID
 * @returns Node ID or handle if found for that id, otherwise null
 */
export const useAppContextNodeHandle = id => {
  // Takes in testID and returns a global reference handle
  const [context] = useContext(AppContext);

  const newReference = context.globalReferences[id];

  return useNodeHandle(newReference);
};

/**
 * Returns context reference from nearest PageContext. Should be used for focusable children on a page.
 * @param {*} id The testID
 * @returns Context reference if found for that id, otherwise null
 */
export const useContextReference = <T = undefined>(testID?: string): MutableRefObject<T> | undefined => {
  // Takes in testID and returns a global reference
  const [context] = useContext(PageContext) || [];

  return (context?.references && context.references[testID]) || undefined;
};

/**
 * Returns context references from nearest PageContext. Should be used for focusable children on a page.
 * @param {*} testIDs A list of testIDs
 * @returns A list of context reference if found for each id. Index is null if not found for that id.
 */
export const useContextReferences = testIDs => {
  // Takes in testID and returns a global reference
  const [context] = useContext(PageContext) || [];

  if (context?.references) {
    return testIDs?.map(testID => context.references[testID]) || null;
  }
  return [];
};

/**
 * Returns all context references from nearest PageContext. Should be used for focusable children on a page.
 * @returns All context references from the nearest PageContext.
 */
export const useAllContextReferences = () => {
  const [context] = useContext(PageContext) || [];

  if (context?.references) {
    return context.references;
  }
  return [];
};

/**
 * Returns node ID/handle from nearest PageContext. Should be used for focuable children on a page.
 * @param {*} id The testID
 * @returns Node ID or handle if found for that id, otherwise null
 */
export const useContextNodeHandle = (testID: string) => {
  // Takes in testID and returns a global reference handle
  const [context] = useContext(PageContext);
  const newReference = context.references[testID];

  return useNodeHandle(newReference);
};

/**
 * Returns a list of node IDs/handles from nearest PageContext. Should be used for focuable children on a page.
 * @param {*} testIDs A list of testIDs
 * @returns A list of Node IDs or handles if found for each id, otherwise that index is null.
 */
export const useContextNodeHandles = testIDs => {
  // Takes in testID and returns a global reference handle
  const [context] = useContext(PageContext) || [];
  const [contextNodeHandles, setContextNodeHandles] = useState([]);
  const newReferences = testIDs.map(testID => (context.references ? context.references[testID] || null : null));

  useEffect(() => {
    const newContextNodeHandles = newReferences.map(reference => findNodeHandle(reference?.current));

    setContextNodeHandles(newContextNodeHandles);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return contextNodeHandles;
};
